/**
 * SolarAPP+ in words (permits/SolarAppPanel.tsx, pages/SolarAppConnection.tsx, the lead timeline): the verdict, the
 * rule results, the submission statuses and what the Submit button may do. No React; tested in solarapp.test.ts.
 */
export type Verdict = "eligible" | "confirm" | "not_eligible";
export type RuleStatus = "pass" | "fail" | "check";
export type SubmissionStatus = "created" | "submitted" | "approved" | "corrections" | "cancelled" | "error";
export type Tone = "ok" | "warn" | "danger" | "info" | undefined;

export interface RuleLike {
  id: string;
  title: string;
  status: RuleStatus;
  detail: string;
}

export function verdictTitle(status: Verdict, toConfirm = 0): string {
  if (status === "eligible") return "Eligible for SolarAPP+";
  if (status === "not_eligible") return "Not eligible for SolarAPP+";
  return `Likely eligible: confirm ${toConfirm} ${toConfirm === 1 ? "item" : "items"}`;
}

export function verdictTone(status: Verdict): Tone {
  return status === "eligible" ? "ok" : status === "confirm" ? "warn" : "danger";
}

export const RULE_LABELS: Record<RuleStatus, string> = { pass: "Pass", fail: "Not eligible", check: "Confirm" };

/** Failing rules first, then the ones to confirm, then the passed ones (each group in the server's order). */
export function orderRules<T extends RuleLike>(rules: T[]): T[] {
  const rank: Record<RuleStatus, number> = { fail: 0, check: 1, pass: 2 };
  return rules.map((r, i) => [r, i] as const).sort((a, b) => rank[a[0].status] - rank[b[0].status] || a[1] - b[1]).map(([r]) => r);
}

export const STATUS_LABELS: Record<SubmissionStatus, string> = {
  created: "Sent to SolarAPP+",
  submitted: "Submitted",
  approved: "Permit issued",
  corrections: "Corrections needed",
  cancelled: "Cancelled",
  error: "Failed",
};

export const STATUS_HINTS: Record<SubmissionStatus, string> = {
  created: "Finish the attestations and pay SolarAPP+'s fee in SolarAPP+.",
  submitted: "SolarAPP+ is reviewing the application.",
  approved: "SolarAPP+ issued the permit; the inspection checklist is in SolarAPP+.",
  corrections: "SolarAPP+ or the AHJ asked for changes: open the project in SolarAPP+.",
  cancelled: "The application was cancelled in SolarAPP+.",
  error: "Solar OS could not reach SolarAPP+ for this application.",
};

export function statusTone(status: SubmissionStatus): Tone {
  return status === "approved" ? "ok" : status === "corrections" || status === "error" ? "danger" : status === "cancelled" ? undefined : "info";
}

/** The timeline line of one status change: "SolarAPP+: Permit issued (Design A)". */
export function timelineLine(status: SubmissionStatus, designName?: string | null): string {
  return `SolarAPP+: ${STATUS_LABELS[status]}${designName ? ` (${designName})` : ""}`;
}

export interface SubmitInput {
  country: string;
  verdict: Verdict;
  apiEnabled: boolean;
  connected: boolean;
  canEdit: boolean;
}

/** What the Submit button shows: hidden (not US), disabled with a reason, or enabled. */
export function submitState(s: SubmitInput): { show: boolean; disabled: boolean; reason: string | null } {
  if (s.country !== "US") return { show: false, disabled: true, reason: null };
  if (!s.apiEnabled) return { show: false, disabled: true, reason: "Submitting from Solar OS opens once SolarAPP+ grants integrator access. Download the prefilled answers and fill SolarAPP+ by hand." };
  if (!s.connected) return { show: true, disabled: true, reason: "Connect your SolarAPP+ account first." };
  if (!s.canEdit) return { show: true, disabled: true, reason: "You can view this design; ask an admin to submit it." };
  if (s.verdict === "not_eligible") return { show: true, disabled: true, reason: "Fix the reasons above first: SolarAPP+ would refuse this design." };
  return { show: true, disabled: false, reason: null };
}
